import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './Admin.css';
import './AdminPrint.css';

const thaiMonths = [
    '', 'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
];

function AdminPrintSlip() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Get params from URL
    const cid = searchParams.get('cid');
    const requestId = searchParams.get('requestId');
    const userName = searchParams.get('name');

    // State
    const [userInfo, setUserInfo] = useState(null);
    const [years, setYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState(null);
    const [months, setMonths] = useState([]);
    const [fromMonth, setFromMonth] = useState(null);
    const [toMonth, setToMonth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [printing, setPrinting] = useState(false);
    const [error, setError] = useState('');

    // Fetch user info and available years
    const fetchData = useCallback(async () => {
        if (!cid) {
            setError('ไม่พบข้อมูลผู้ใช้');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            // Fetch available years for this user
            const yearsResponse = await api.get(`/salary/admin/available-years/${cid}`);
            setYears(yearsResponse.data);

            if (yearsResponse.data.length > 0) {
                setSelectedYear(yearsResponse.data[0]);
            }

            setUserInfo({
                cid: cid,
                name: userName || 'ผู้ใช้'
            });
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('ไม่สามารถโหลดข้อมูลได้');
        } finally {
            setLoading(false);
        }
    }, [cid, userName]);

    // Fetch months when year changes
    const fetchMonths = useCallback(async () => {
        if (!selectedYear || !cid) return;

        try {
            const response = await api.get(`/salary/admin/available-months/${cid}/${selectedYear}`);
            setMonths(response.data);

            // Reset selection
            if (response.data.length > 0) {
                setFromMonth(response.data[0].mm);
                setToMonth(response.data[response.data.length - 1].mm);
            } else {
                setFromMonth(null);
                setToMonth(null);
            }
        } catch (err) {
            console.error('Error fetching months:', err);
            setMonths([]);
        }
    }, [selectedYear, cid]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        fetchMonths();
    }, [fetchMonths]);

    // Get months in range
    const getSelectedMonths = () => {
        if (!fromMonth || !toMonth) return [];
        const start = Math.min(fromMonth, toMonth);
        const end = Math.max(fromMonth, toMonth);
        return months.filter(m => m.mm >= start && m.mm <= end);
    };

    // Calculate total net pay for selected months
    const calculateTotal = () => {
        return getSelectedMonths().reduce((sum, m) => sum + (m.cnet || 0), 0);
    };

    // Handle print
    const handlePrint = async () => {
        const selectedMonths = getSelectedMonths();
        if (selectedMonths.length === 0) return;

        setPrinting(true);
        try {
            const monthsData = selectedMonths.map(m => ({ yyyy: selectedYear, mm: m.mm }));

            const response = await api.post('/report/admin/slip-multiple', {
                cid: cid,
                months: monthsData
            }, {
                responseType: 'blob',
                timeout: 60000 // 60 second timeout
            });

            // Check if response is actually a PDF or an error JSON
            const contentType = response.headers['content-type'];
            if (contentType && contentType.includes('application/json')) {
                // This is an error response
                const text = await response.data.text();
                const errorData = JSON.parse(text);
                throw new Error(errorData.error || 'เกิดข้อผิดพลาด');
            }

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (err) {
            console.error('Error generating PDF:', err);
            const errorMessage = err.response?.data?.error || err.message || 'เกิดข้อผิดพลาดในการสร้าง PDF';
            alert(errorMessage);
        } finally {
            setPrinting(false);
        }
    };

    if (loading) {
        return (
            <div className="admin-page">
                <div className="flex-center" style={{ padding: '60px 0' }}>
                    <div className="spinner"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-page">
                <div className="alert alert-error">{error}</div>
                <button className="steam-btn steam-btn-secondary" onClick={() => navigate(-1)}>
                    กลับ
                </button>
            </div>
        );
    }

    const selectedMonthsCount = getSelectedMonths().length;

    return (
        <div className="admin-page admin-print-page">
            {/* Breadcrumb */}
            <div className="breadcrumb">
                <Link to="/admin">จัดการระบบ</Link>
                <span>/</span>
                <Link to="/admin/requests">คำร้องขอเอกสาร</Link>
                <span>/</span>
                <span>พิมพ์สลิปเงินเดือน</span>
            </div>

            {/* Header */}
            <div className="print-page-header">
                <div className="print-page-icon slip">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                    </svg>
                </div>
                <div className="print-page-title">
                    <h1>พิมพ์สลิปเงินเดือน</h1>
                    <p>เลือกช่วงเดือนที่ต้องการพิมพ์สำหรับผู้ใช้</p>
                </div>
            </div>

            {/* User Info Card */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">ข้อมูลผู้ใช้</div>
                <div className="steam-card-body">
                    <div className="user-info-display">
                        <div className="user-info-avatar">
                            {userInfo?.name?.charAt(0) || 'U'}
                        </div>
                        <div className="user-info-details">
                            <h3>{userInfo?.name}</h3>
                            <span className="user-info-cid">{userInfo?.cid}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Year Selection */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">เลือกปี</div>
                <div className="steam-card-body">
                    {years.length === 0 ? (
                        <div className="empty-state">
                            <p>ไม่พบข้อมูลเงินเดือน</p>
                        </div>
                    ) : (
                        <div className="year-buttons">
                            {years.map(year => (
                                <button
                                    key={year}
                                    className={`year-btn ${selectedYear === year ? 'active' : ''}`}
                                    onClick={() => setSelectedYear(year)}
                                >
                                    {year + 543}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Month Range Selection */}
            {selectedYear && months.length > 0 && (
                <div className="steam-card mb-3">
                    <div className="steam-card-header">เลือกช่วงเดือน</div>
                    <div className="steam-card-body">
                        <div className="month-range-selector">
                            <div className="range-group">
                                <label>จากเดือน</label>
                                <select
                                    className="steam-select"
                                    value={fromMonth || ''}
                                    onChange={(e) => setFromMonth(parseInt(e.target.value))}
                                >
                                    {months.map(m => (
                                        <option key={m.mm} value={m.mm}>
                                            {thaiMonths[m.mm]} {selectedYear + 543}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="range-separator">
                                <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                                    <path d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"/>
                                </svg>
                            </div>
                            <div className="range-group">
                                <label>ถึงเดือน</label>
                                <select
                                    className="steam-select"
                                    value={toMonth || ''}
                                    onChange={(e) => setToMonth(parseInt(e.target.value))}
                                >
                                    {months.map(m => (
                                        <option key={m.mm} value={m.mm}>
                                            {thaiMonths[m.mm]} {selectedYear + 543}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Quick Selection */}
                        <div className="quick-select-buttons">
                            <button
                                className="steam-btn steam-btn-secondary steam-btn-sm"
                                onClick={() => {
                                    if (months.length > 0) {
                                        setFromMonth(months[0].mm);
                                        setToMonth(months[0].mm);
                                    }
                                }}
                            >
                                เดือนแรก
                            </button>
                            <button
                                className="steam-btn steam-btn-secondary steam-btn-sm"
                                onClick={() => {
                                    if (months.length > 0) {
                                        const lastMonth = months[months.length - 1].mm;
                                        setFromMonth(lastMonth);
                                        setToMonth(lastMonth);
                                    }
                                }}
                            >
                                เดือนล่าสุด
                            </button>
                            <button
                                className="steam-btn steam-btn-secondary steam-btn-sm"
                                onClick={() => {
                                    if (months.length > 0) {
                                        setFromMonth(months[0].mm);
                                        setToMonth(months[months.length - 1].mm);
                                    }
                                }}
                            >
                                ทั้งปี
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Selected Months Preview */}
            {selectedMonthsCount > 0 && (
                <div className="steam-card mb-3">
                    <div className="steam-card-header">
                        <span>เดือนที่เลือก</span>
                        <span className="badge badge-info">{selectedMonthsCount} เดือน</span>
                    </div>
                    <div className="steam-card-body" style={{ padding: 0 }}>
                        <div className="selected-months-list">
                            {getSelectedMonths().map(m => (
                                <div key={m.mm} className="selected-month-item">
                                    <span className="month-name">{thaiMonths[m.mm]} {selectedYear + 543}</span>
                                    <span className="month-net">{(m.cnet || 0).toLocaleString()} บาท</span>
                                </div>
                            ))}
                        </div>
                        <div className="selected-months-total">
                            <span>รวมรับสุทธิ</span>
                            <span className="total-value">{calculateTotal().toLocaleString()} บาท</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Print Actions */}
            <div className="print-actions-footer">
                <button
                    className="steam-btn steam-btn-secondary"
                    onClick={() => navigate(-1)}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                    กลับ
                </button>
                <button
                    className="steam-btn steam-btn-primary print-main-btn"
                    onClick={handlePrint}
                    disabled={selectedMonthsCount === 0 || printing}
                >
                    {printing ? (
                        <>
                            <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }}></div>
                            กำลังสร้าง PDF...
                        </>
                    ) : (
                        <>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <path d="M6 9V2h12v7"/>
                                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                                <rect x="6" y="14" width="12" height="8"/>
                            </svg>
                            พิมพ์สลิปเงินเดือน ({selectedMonthsCount} เดือน)
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}

export default AdminPrintSlip;
